import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed } from 'vue';
import type { IPriceOption } from '@/interface/IOdds';
import type { ISingleTicket } from '@/interface/ITicket';
import { MarketType, OddsOption, SportType } from '@/interface/enum';
import { getTranslated } from '@/libraries/helpers/translationHelper';
import { i18n } from '@/libraries/i18n';
import { isCorrectScoreMarket, isFormatPoint, isShowPointGroup, isShowPointMarket } from '@/libraries/oddsDisplay';
import PriceOptionLib from '@/libraries/priceOption';
import { formatMinDecimalPlace } from '@/libraries/utils/text';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';

export function useTicketDisplay(
    ticket: Ref<ISingleTicket>,
    options: { displayPriceValue?: Ref<number> } = {},
) {
    const priceOption = computed<IPriceOption>(() => (
        ticket.value.odds.priceOption.find(item => item.option === ticket.value.option)
        ?? {
            option: ticket.value.option,
            oddsOption: OddsOption.fromOddsApi(
                MarketType.toOddsApi(ticket.value.odds.marketType),
                ticket.value.option,
            ),
            optionName: '',
            optionNameShort: '',
            price: ticket.value.price,
        }
    ));

    const leagueName = computed(() => getTranslated(ticket.value.event.leagueName));
    const homeTeam = computed(() => getTranslated(ticket.value.event.homeTeam?.teamName));
    const awayTeam = computed(() => getTranslated(ticket.value.event.awayTeam?.teamName));
    const marketGroupName = computed(() => getTranslated(ticket.value.odds.marketGroup.name));

    const isCorrectScore = computed(() => isCorrectScoreMarket(ticket.value.odds.marketType));
    const isCorrectScoreAos = computed(() => isCorrectScore.value && priceOption.value.oddsOption === OddsOption.CS_Others && ticket.value.odds.isLive);

    const displayPriceValue = computed(() => (
        options.displayPriceValue?.value ?? ticket.value.price
    ));
    const displayPrice = computed(() => formatMinDecimalPlace(
        displayPriceValue.value,
        isCorrectScore.value ? 0 : 2,
    ));
    const isOddsNegative = computed(() => displayPriceValue.value < 0);

    const { isEuroSite } = storeToRefs(useFeatureToggleStore());
    const option = computed(() => {
        const { optionName } = priceOption.value;
        if (optionName === 'odds_option_home') {
            return homeTeam.value;
        }
        if (optionName === 'odds_option_away') {
            return awayTeam.value;
        }

        if (isCorrectScore.value) {
            const split = optionName.split(';');
            const betOption = ticket.value.option.split(':');
            const isSecondHalf = ticket.value.odds.marketType === MarketType.SH_CorrectScore;

            const homeScore = isSecondHalf ? Number(betOption[0]) : Number(betOption[0]) + ticket.value.odds.eventResult.liveHomeScore;
            const awayScore = isSecondHalf ? Number(betOption[1]) : Number(betOption[1]) + ticket.value.odds.eventResult.liveAwayScore;

            return i18n.t(split[0], {
                home: homeScore,
                away: awayScore,
            });
        }

        if (isEuroSite.value && ticket.value.odds.marketType === MarketType.HTFT) {
            return PriceOptionLib.getEuroHtFtOption(optionName);
        }

        if (PriceOptionLib.betOptionUseTemplate(ticket.value.odds.marketType, ticket.value.odds.marketGroup)) {
            return optionName;
        }

        return PriceOptionLib.getOptionName(
            optionName,
            ticket.value.event,
            ticket.value.odds.marketType,
        );
    });

    const isShowPoint = computed(() => (
        isShowPointMarket(ticket.value.odds.marketType)
        && isShowPointGroup(ticket.value.odds.marketGroup.id)
    ));

    const point = computed(() => {
        if (!isShowPoint.value) {
            return '';
        }
        if (isFormatPoint(ticket.value.odds.marketType) && priceOption.value.oddsOption === OddsOption.Home) {
            return -ticket.value.odds.point;
        }
        return ticket.value.odds.point;
    });

    const isShowLiveScore = computed(() => {
        if (!ticket.value.odds.isLive) {
            return false;
        }

        if (ticket.value.event.sportType === SportType.Cricket) {
            return false;
        }

        if (ticket.value.odds.marketType === MarketType.SH_CorrectScore) {
            return false;
        }
        return true;
    });

    return {
        priceOption,
        leagueName,
        homeTeam,
        awayTeam,
        marketGroupName,
        isCorrectScoreAos,
        displayPrice,
        isOddsNegative,
        option,
        point,
        isShowLiveScore,
    };
}
